import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, ListChecks, RefreshCw } from "lucide-react";

/**
 * AssessmentLaneChoice — the participant-facing screen shown before the
 * assessment when the user has not yet chosen a lane, OR when they visit
 * `/assessment/choose` deliberately. Neither lane is presented as
 * superior, but the honest differences are stated in the participant's
 * own words.
 *
 * NO estimated completion times are shown. The user's brief explicitly
 * forbids this unless measured; we have not measured, so we do not show.
 */
export default function AssessmentLaneChoice() {
  const [lanes, setLanes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choosing, setChoosing] = useState(null); // 'quick' | 'complete'
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/assessment/lanes");
      setLanes(data);
    } catch (err) {
      setError(extractApiError(err, "Couldn't load your Career Repath Assessment comparison."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const choose = async (lane) => {
    setChoosing(lane);
    setError(null);
    try {
      await api.post("/assessment/lane/set", { lane });
      navigate(`/assessment?lane=${lane}`);
    } catch (err) {
      // 402 = Complete Assessment paywall gate. Route the participant
      // to the pricing page with a gated_from marker so they land in
      // context. Their Quick Repath answers stay intact (matching
      // answers carry over on purchase).
      const status = err?.response?.status;
      if (lane === "complete" && status === 402) {
        navigate("/plans?gated_from=complete-assessment");
        return;
      }
      setError(extractApiError(err, "Couldn't save your choice."));
      setChoosing(null);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div data-testid="lane-choice-loading" className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading your options…
        </div>
      </AppShell>
    );
  }

  if (error && !lanes) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <Alert variant="destructive" data-testid="lane-choice-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-6">
            <Button onClick={load} data-testid="lane-choice-retry" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" /> Try again
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  const { quick, complete, current_lane, notes } = lanes;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-wider text-fg-secondary">Choose how you'd like to start</p>
        <h1
          data-testid="lane-choice-title"
          className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          Two ways to begin.
        </h1>
        <p className="mt-3 text-base sm:text-lg text-fg-secondary leading-relaxed">
          Both paths are equally dignified. Pick the starting point that feels
          right for you today. You can continue from the Quick Repath to the
          Complete Career Repath Assessment at any time — any matching answers
          carry over automatically.
        </p>

        {error && (
          <Alert variant="destructive" className="mt-6" data-testid="lane-choice-inline-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <LaneCard
            testid="lane-card-quick"
            lane={quick}
            isCurrent={current_lane === "quick"}
            choosing={choosing === "quick"}
            disabled={!!choosing}
            onChoose={() => choose("quick")}
            ctaLabel="Start free — 18 questions"
          />
          <LaneCard
            testid="lane-card-complete"
            lane={complete}
            isCurrent={current_lane === "complete"}
            choosing={choosing === "complete"}
            disabled={!!choosing}
            onChoose={() => {
              if (complete?.locked) {
                navigate("/plans?gated_from=complete-assessment");
                return;
              }
              choose("complete");
            }}
            ctaLabel={
              complete?.locked
                ? `Unlock complete assessment — ${complete?.price_display || "$24.99"}`
                : "Start Complete Career Repath Assessment"
            }
            locked={complete?.locked}
            lockedReason={complete?.locked_reason}
            priceDisplay={complete?.price_display}
            emphasis
          />
        </div>

        {(notes || []).length > 0 && (
          <div className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-4" data-testid="lane-choice-notes">
            <p className="text-xs uppercase tracking-wider text-fg-secondary mb-2">Good to know</p>
            <ul className="text-sm text-fg-secondary space-y-1">
              {notes.map((n, i) => <li key={i} data-testid={`lane-note-${i}`}>· {n}</li>)}
            </ul>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function LaneCard({ testid, lane, isCurrent, choosing, disabled, onChoose, ctaLabel, emphasis, locked, lockedReason, priceDisplay }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg border p-6 flex flex-col ${
        emphasis ? "border-accent-warm/40 bg-bg-surface" : "border-border-subtle bg-bg-surface"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm text-fg-secondary" data-testid={`${testid}-label`}>
          {lane.label}
        </p>
        {isCurrent && (
          <span data-testid={`${testid}-current-chip`} className="text-xs px-2 py-0.5 rounded-full bg-progress-teal-bg text-progress-teal-hi border border-progress-teal/30">
            Your current path
          </span>
        )}
        {locked && !isCurrent && (
          <span
            data-testid={`${testid}-locked-chip`}
            className="text-xs px-2 py-0.5 rounded-full bg-accent-warm/10 text-accent-warm border border-accent-warm/30"
          >
            {priceDisplay || "$24.99 one-time"}
          </span>
        )}
      </div>
      <p className="mt-2 text-lg font-heading text-fg-primary" data-testid={`${testid}-question-count`}>
        {lane.question_count} questions · {lane.sections} sections
      </p>
      <p className="mt-2 text-sm text-fg-secondary" data-testid={`${testid}-oneliner`}>
        {lane.one_liner}
      </p>
      <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
        {lane.detail}
      </p>

      <dl className="mt-4 text-sm space-y-1">
        <div className="flex justify-between">
          <dt className="text-fg-secondary">Result:</dt>
          <dd className="text-fg-primary text-right ml-3" data-testid={`${testid}-result`}>{lane.result}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-fg-secondary">Your matching answers already saved:</dt>
          <dd className="text-fg-primary tabular-nums" data-testid={`${testid}-carryover`}>{lane.answered_carryover}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-fg-secondary">Unlocks pathway direction recommendation:</dt>
          <dd className="text-fg-primary" data-testid={`${testid}-unlocks`}>{lane.unlocks_directions ? "Yes" : "No"}</dd>
        </div>
      </dl>

      {locked && lockedReason && (
        <p
          data-testid={`${testid}-locked-reason`}
          className="mt-4 rounded-md border border-accent-warm/30 bg-accent-warm/5 p-3 text-xs text-fg-primary leading-relaxed"
        >
          {lockedReason}
        </p>
      )}

      <div className="mt-6">
        <Button
          onClick={onChoose}
          disabled={disabled}
          data-testid={`${testid}-choose-btn`}
          className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
        >
          {choosing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4 mr-2" aria-hidden="true" />}
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}
